<template>
  <v-menu
    ref="menu"
    v-model="isActive"
    :close-on-content-click="false"
    :content-class="$vnode.data.staticClass"
    :disabled="disabled"
    transition="scale-transition"
    offset-y
    :nudge-top="110"
    max-width="290px"
    min-width="290px"
    @update:return-value="onUpdate(model)"
  >
    <template #activator="{ on, attrs }">
      <slot name="input" :on="on" :attrs="attrs">
        <BTextField
          ref="input"
          v-bind="Object.assign({}, attrs, $attrs)"
          :value="formattedDate"
          :skeleton="skeleton"
          :loading="loading"
          :prepend-inner-icon="icon"
          autocomplete="off"
          :name="name"
          :disabled="disabled"
          :class="$vnode.data.staticClass"
          v-on="Object.assign({}, on, listeners)"
          @click="onClick"
          @click:clear="onClear"
          @input="onInput"
          @focus="focus"
        >
          <template v-for="(_, key) in $slots" #[key]>
            <slot :name="key" />
          </template>

          <template v-for="(_, key) in $scopedSlots" #[key]="props">
            <slot v-bind="props" :name="key" />
          </template>
        </BTextField>
      </slot>
    </template>

    <v-date-picker
      v-if="showDate"
      v-model="modelPicker"
      :min="minDate"
      :max="maxDate"
      :first-day-of-week="1"
      no-title
      scrollable
      @input="onChangePicker"
    />

    <v-time-picker
      v-if="showTime"
      v-model="modelTimePicker"
      format="24hr"
      scrollable
      @input="onChangeTimePicker"
    />

    <slot name="after" />

    <template v-if="datetime || withButtons">
      <v-spacer />
      <div :class="buttonsClass">
        <v-btn
          v-if="datetime"
          text
          icon
          @click="toggleView"
        >
          <v-icon v-if="showDate">
            mdi-clock-outline
          </v-icon>
          <v-icon v-else>
            mdi-calendar
          </v-icon>
        </v-btn>
        <v-btn v-if="withButtons" text @click="onCancel" v-text="'Отмена'" />
        <v-btn v-if="withButtons" depressed @click="onSave" v-text="'Изменить'" />
      </div>
    </template>
  </v-menu>
</template>

<script>
import moment from 'moment/moment'
import uniqueId from 'lodash/uniqueId'
import { dateFormatterIgnoreTz, dateFromFormatter, dateIsValid } from '@/helpers/dateFormatter'
import BTextField from './BTextField.vue'


const DATE_FORMAT = 'YYYY-MM-DD'
const TIME_FORMAT = 'HH:mm'
const DATETIME_FORMAT = 'YYYY-MM-DDTHH:mm'

const DATE_FORMAT_VALUE = 'DD.MM.YYYY'
const DATETIME_FORMAT_VALUE = 'DD.MM.YYYY HH:mm'

const VIEW_DATE = 'VIEW_DATE'
const VIEW_TIME = 'VIEW_TIME'

export default {
  name: 'BTextFieldDate',
  inject: ['$validator'],
  components: {
    BTextField,
  },
  inheritAttrs: false,
  props: {
    value: null,
    disabled: Boolean,
    skeleton: Boolean,
    withButtons: Boolean,
    closeOnDateSelect: {
      type: Boolean,
      default: true,
    },
    datetime: Boolean,
    min: [String, Date],
    max: [String, Date],
    loading: Boolean,
    icon: {
      type: String,
      default: 'mdi-calendar',
    },
    format: {
      type: String,
      default() {
        return this.$props.datetime
          ? DATETIME_FORMAT_VALUE
          : DATE_FORMAT_VALUE
      },
    },
    formatReturned: {
      type: String,
      default() {
        return this.$props.datetime
          ? DATETIME_FORMAT
          : DATE_FORMAT
      },
    },
    name: {
      type: String,
      default: () => uniqueId('b-text-field-date-'),
    },
  },
  data() {
    const listeners = { ...this.$listeners }
    delete listeners.input

    let value = null
    // В ФСД кто-то передал вместо строки - число
    // То есть значение существует, а includes - нет. Добавили проверку на includes.
    if (typeof this.value === 'string') {
      value = this.value?.includes('.') ? this.value.split('.').reverse().join('-') : this.value
    }

    return {
      isActive: false,
      model: value,
      modelPicker: this.isValueCorrect(value) ? value : moment().format(DATE_FORMAT),
      modelTimePicker: value,
      view: VIEW_DATE,
      listeners,
    }
  },
  computed: {
    formattedDate() {
      return dateFormatterIgnoreTz(this.model, this.format, null)
    },
    minDate() {
      return dateFormatterIgnoreTz(this.min, DATE_FORMAT)
    },
    maxDate() {
      return dateFormatterIgnoreTz(this.max, DATE_FORMAT)
    },
    showDate() {
      return this.view === VIEW_DATE
    },
    showTime() {
      return this.datetime && this.view === VIEW_TIME
    },
    buttonsClass() {
      return { 'v-picker-actions': true, 'justify-center': !this.withButtons }
    },
  },
  watch: {
    value: {
      immediate: true,
      handler: 'onChangeValue',
    },
  },
  methods: {
    focus() {
      this.isActive = true
    },
    isValueCorrect(value = this.value) {
      if (typeof value === 'string') {
        return (
          moment(value, this.format, true).isValid()
        || moment(value, this.formatReturned).isValid()
        )
      }
      return false
    },
    onUpdate(newValue) {
      const value = dateFormatterIgnoreTz(newValue, this.formatReturned, null)

      if (moment(newValue, DATE_FORMAT, true).isValid()
          || moment(newValue, DATETIME_FORMAT, true).isValid()
          || moment(newValue, DATE_FORMAT_VALUE, true).isValid()
          || moment(newValue, DATETIME_FORMAT_VALUE, true).isValid()
      ) {
        this.modelPicker = dateFormatterIgnoreTz(newValue, DATE_FORMAT)
        if (moment(newValue, DATETIME_FORMAT, true).isValid()) {
          this.modelTimePicker = dateFormatterIgnoreTz(newValue, TIME_FORMAT)
        }
      }

      if (value === this.value) {
        this.view = VIEW_DATE
        return
      }

      const date = dateFormatterIgnoreTz(this.modelPicker, DATE_FORMAT)
      const time = dateFormatterIgnoreTz(this.modelTimePicker, [TIME_FORMAT, TIME_FORMAT])
      this.model = date && time ? `${date}T${time}` : null
      this.$emit('input', value)
    },
    onClear() {
      this.view = VIEW_DATE
      this.value = null
      this.model = null
      this.modelPicker = null
      this.modelTimePicker = null
      this.$emit('input', null)
    },
    onInput(e) {
      if (e?.length <= 0) {
        this.onClear()
        return
      }

      const isValid = !!(
        e?.length === this.format?.length
        && dateIsValid(e, this.format)
      )

      if (!isValid) return

      const ms = +new Date(dateFormatterIgnoreTz(e, [this.format]))
      const { minDate, maxDate } = this

      if (minDate && new Date(minDate) > ms) {
        return
      }

      if (maxDate && new Date(maxDate) < ms) {
        return
      }

      const date = dateFromFormatter(e, this.format)
      this.onUpdate(date)
    },
    onChangeValue() {
      const { value } = this
      if (this.isValueCorrect(value)) {
        this.model = dateFormatterIgnoreTz(value, this.formatReturned)

        this.modelPicker = dateFormatterIgnoreTz(value, DATE_FORMAT)
        this.modelTimePicker = dateFormatterIgnoreTz(value, TIME_FORMAT)
        if (!this.withButtons && !this.datetime && this.closeOnDateSelect && value) this.onSave()
      }
    },
    onChangePicker(date) {
      if (!this.modelTimePicker) this.modelTimePicker = (new Date()).toLocaleTimeString()
      const time = dateFormatterIgnoreTz(this.modelTimePicker, [TIME_FORMAT, TIME_FORMAT])
      const datetime = `${date}T${time}`
      if (!this.withButtons) this.onUpdate(datetime)
    },
    onChangeTimePicker(time) {
      if (!this.model) return
      const date = dateFormatterIgnoreTz(this.model, DATE_FORMAT)
      const datetime = `${date}T${time}`
      if (!this.withButtons) this.onUpdate(datetime)
    },
    async onClick() {
      this.isActive = true
      await this.$nextTick()
      this.isActive = true
      await new Promise((resolve) => setTimeout(resolve, 0))
      this.isActive = true

      // Если текущая дата стоит раньше мин. допустимой даты, то в Picker ставим мин. дату
      if (!this.model || moment(this.model).isBefore(this.minDate)) {
        this.modelPicker = this.minDate
      }
    },
    toggleView() {
      this.view = this.view === VIEW_TIME ? VIEW_DATE : VIEW_TIME
    },
    onSave() {
      this.isActive = false
      const date = dateFormatterIgnoreTz(this.modelPicker, DATE_FORMAT)
      if (!this.modelTimePicker) {
        this.modelTimePicker = dateFormatterIgnoreTz((new Date()).toLocaleTimeString(), [TIME_FORMAT, TIME_FORMAT])
      }
      const time = this.modelTimePicker
      this.model = `${date}T${time}`
      this.view = VIEW_DATE
      this.$refs.menu.save(this.model)
    },
    onCancel() {
      this.isActive = false
      this.modelPicker = dateFormatterIgnoreTz(this.model, DATE_FORMAT)
      this.modelTimePicker = dateFormatterIgnoreTz(this.model, TIME_FORMAT)
      this.view = VIEW_DATE
    },
  },
}
</script>

<style lang="scss">
$bgWhite: #a6b9dc;

.v-picker-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0.5rem;
  background: $bgWhite;

  &.justify-center {
    justify-content: center;
  }
}

.v-date-picker-table .v-btn.v-btn--active {
  color: $bgWhite !important;
}
</style>
